import type {
  AgentSnapshot,
  DeliveryMode,
  ModelOption,
  OutputMode,
} from "@remote-agents/shared";
import { MODEL_CATALOG, modelLabel, modelProvider } from "@remote-agents/shared";
import {
  buildExecutionView,
  displayExecutionModel,
  providerLabel,
  resolveAgentProvider,
} from "../execution";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ActivityFeed } from "../components/ActivityFeed";
import { MarkdownWorkspace } from "../components/MarkdownWorkspace";
import { MessageBox } from "../components/MessageBox";
import { StatusHero } from "../components/StatusHero";
import {
  AuthError,
  fetchAgent,
  fetchModels,
  sendAgentMessage,
  sendClaudeMessage,
  setAgentCwd,
  setAgentModel,
  setAgentOutputMode,
  stopClaude,
} from "../api";
import { useAuth } from "../auth";
import {
  agentWithModel,
  displayModel,
  formatClock,
  formatElapsed,
  isAgentSnapshot,
  mergeAgentLists,
  readPrompt,
  rememberPrompt,
  statusLabel,
  writeLastAgent,
} from "../format";
import { useNow } from "../hooks";
import { useLive } from "../live";
import { useToast } from "../toast";

const QUICK_MODELS = [
  { id: "claude-fable-5-1", short: "Fable" },
  { id: "grok-4.6", short: "Grok 4.6" },
  { id: "composer-2.5", short: "Composer 2.5" },
] as const;

function DetailSkeleton() {
  return (
    <div className="page">
      <div className="skeleton-stack">
        <div className="skeleton skeleton--hero" />
        <div className="skeleton skeleton--block" />
        <div className="skeleton skeleton--block" />
      </div>
    </div>
  );
}

export function AgentDetail() {
  const { id = "" } = useParams();
  const { agents, refresh, replaceAgent } = useLive();
  const { markSignedOut } = useAuth();
  const toast = useToast();
  const fromLive = agents.find((a) => a.id === id);
  const [local, setLocal] = useState(fromLive);
  const [models, setModels] = useState<ModelOption[]>(MODEL_CATALOG);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [claudeBusy, setClaudeBusy] = useState(false);
  const [cwdDraft, setCwdDraft] = useState("");
  const [pinnedPrompt, setPinnedPrompt] = useState<string | null>(() =>
    id ? readPrompt(id) : null
  );
  const [instructionDraft, setInstructionDraft] = useState<string | null>(null);

  const agent = local && local.id === id ? local : fromLive;
  const running =
    agent?.status === "running" ||
    agent?.status === "starting" ||
    agent?.status === "queued";
  const now = useNow(1000, Boolean(running));
  const execution = agent ? buildExecutionView(agent, now) : null;

  useEffect(() => {
    if (id) {
      writeLastAgent(id);
      setPinnedPrompt(readPrompt(id));
    }
  }, [id]);

  useEffect(() => {
    if (fromLive && fromLive.id === id) setLocal(fromLive);
  }, [fromLive, id]);

  useEffect(() => {
    if (agent?.cwd) setCwdDraft(agent.cwd);
  }, [agent?.cwd]);

  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    setLoadError(null);
    fetchAgent(id)
      .then((next) => {
        if (!cancelled) {
          setLocal(next);
          replaceAgent(next);
        }
      })
      .catch((err) => {
        if (err instanceof AuthError) markSignedOut();
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Agent unavailable");
      });
    fetchModels()
      .then((next) => {
        if (!cancelled && next.length) setModels(next);
      })
      .catch((err) => {
        if (err instanceof AuthError) markSignedOut();
      });
    return () => {
      cancelled = true;
    };
  }, [id, markSignedOut, replaceAgent]);

  const catalog = useMemo(() => {
    if (!agent) return models;
    const provider = resolveAgentProvider(agent);
    const base = MODEL_CATALOG.filter(
      (entry) =>
        modelProvider(entry.id) === provider ||
        (provider === "claude" && entry.id.startsWith("claude-code:"))
    );
    const merged = [...base, ...models].filter((entry) => modelProvider(entry.id) === provider);
    const extra = (agent.availableModels ?? [])
      .filter((mid) => modelProvider(mid) === provider && !merged.some((entry) => entry.id === mid))
      .map((mid) => ({
        id: mid,
        label: modelLabel(mid),
        short: modelLabel(mid),
      }));
    const seen = new Set<string>();
    return [...merged, ...extra].filter((entry) => {
      if (seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
  }, [agent, models]);

  function patchAgent(patch: Partial<AgentSnapshot>) {
    if (!agent) return;
    const next = { ...agent, ...patch };
    setLocal(next);
    replaceAgent(next);
  }

  async function guarded<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof AuthError) {
        markSignedOut();
      }
      throw err;
    }
  }

  async function onSend(text: string, delivery: DeliveryMode) {
    if (!agent) return;
    await guarded(async () => {
      await sendAgentMessage(id, text, delivery);
      rememberPrompt(id, text);
      setPinnedPrompt(text);
      if (delivery === "queue") {
        toast.push(running ? `Queued for ${agent.name}` : `Sent to ${agent.name}`, "success");
      } else {
        toast.push(`Interrupt sent to ${agent.name}`, "success");
      }
      void refresh();
    });
  }

  async function onModel(model: string) {
    if (!agent || !model || model === agent.model) return;
    const prev = agent.model;
    setSettingsBusy(true);
    patchAgent({ model });
    try {
      await guarded(async () => {
        const next = await setAgentModel(id, model);
        if (isAgentSnapshot(next)) {
          setLocal(next);
          replaceAgent(next);
        }
        toast.push(`Model set to ${displayModel(model)}`, "success");
      });
    } catch (err) {
      patchAgent({ model: prev });
      toast.push(
        err instanceof Error ? err.message : "Model change failed",
        "error"
      );
    } finally {
      setSettingsBusy(false);
    }
  }

  async function onOutputMode(outputMode: OutputMode) {
    if (!agent || outputMode === agent.outputMode) return;
    const prev = agent.outputMode;
    setSettingsBusy(true);
    patchAgent({ outputMode });
    try {
      await guarded(async () => {
        await setAgentOutputMode(id, outputMode);
      });
    } catch (err) {
      patchAgent({ outputMode: prev });
      toast.push(
        err instanceof Error ? err.message : "Could not switch log detail",
        "error"
      );
    } finally {
      setSettingsBusy(false);
    }
  }

  async function onCwd() {
    if (!agent) return;
    const nextCwd = cwdDraft.trim();
    if (!nextCwd || nextCwd === agent.cwd) return;
    setSettingsBusy(true);
    try {
      await guarded(async () => {
        await setAgentCwd(id, nextCwd);
        patchAgent({ cwd: nextCwd });
        toast.push(`Folder changed to ${nextCwd}`, "success");
      });
    } catch (err) {
      toast.push(
        err instanceof Error ? err.message : "Folder change failed",
        "error"
      );
    } finally {
      setSettingsBusy(false);
    }
  }

  async function onClaude(text: string, delivery: DeliveryMode) {
    await guarded(async () => {
      await sendClaudeMessage(id, text, delivery);
      toast.push(`Claude Code prompt sent to ${agent?.name ?? "agent"}`, "success");
      void refresh();
    });
  }

  async function onStopClaude(delivery: DeliveryMode) {
    setClaudeBusy(true);
    try {
      await guarded(async () => {
        await stopClaude(id, delivery);
        toast.push("Claude Code stop requested", "success");
        void refresh();
      });
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "Stop failed", "error");
    } finally {
      setClaudeBusy(false);
    }
  }

  if (!agent) {
    return loadError ? <div className="page"><h1>Agent unavailable</h1><p role="alert">{loadError}</p><Link to="/agents" className="btn">Back to agents</Link></div> : <DetailSkeleton />;
  }

  function onReviewLastInstruction() {
    const last = readPrompt(id);
    if (!last) {
      toast.push("No previous instruction to review", "error");
      return;
    }
    setInstructionDraft(last);
    toast.push("Last instruction loaded for review", "success");
  }

  return (
    <div className="page page--detail">
      <div className="agent-sticky">
        <Link to="/agents" className="agent-sticky__back" aria-label="All agents">
          ‹
        </Link>
        <span className="agent-sticky__name">{agent.name}</span>
        <span className={`status-pill status-pill--${agent.status}`}>
          {execution?.statusLabel ?? statusLabel(agent.status)}
        </span>
        {execution?.elapsedMs !== null && agent.runStartedAt ? (
          <span className="agent-sticky__elapsed">
            {formatElapsed(agent.runStartedAt, now)}
          </span>
        ) : null}
        {execution?.phase === "running" && execution.toolCount > 0 ? (
          <span className="agent-sticky__tools">{execution.toolCount} tools</span>
        ) : null}
        {execution?.isStale ? (
          <span className="chip chip--warn chip--sm">Stale</span>
        ) : null}
      </div>

      <header className="page__head">
        <div>
          <p className="eyebrow">
            <Link to="/overview">Overview</Link> / {agent.kind}
          </p>
          <h1>{agent.name}</h1>
          <p className="page-lede">
            {providerLabel(execution?.provider ?? resolveAgentProvider(agent))} ·{" "}
            {displayExecutionModel(agent.model)}
          </p>
        </div>
      </header>

      <StatusHero
        agent={agent}
        now={now}
        onReviewLastInstruction={onReviewLastInstruction}
      />

      <section className="card">
        <h2>Send instruction</h2>
        <p className="muted">
          Queue runs after the current task. Interrupt stops it and runs now.
        </p>
        <MessageBox
          key={agent.id}
          running={Boolean(running)}
          disabled={agent.status === "offline"}
          draft={instructionDraft}
          onSend={onSend}
          placeholder={`Tell ${agent.name} what to do…`}
        />
      </section>

      <ActivityFeed
        condensedLog={agent.condensedLog}
        fullLog={agent.fullLog}
        summary={agent.summary}
        outputMode={agent.outputMode}
        onToggle={(m) => void onOutputMode(m)}
        toggleBusy={settingsBusy}
        pinnedPrompt={pinnedPrompt}
        running={agent.status === "running" || agent.status === "starting"}
        lastActions={agent.lastActions}
        stale={execution?.isStale}
        lastUpdateAt={execution?.lastUpdateMs ?? undefined}
        now={now}
      />

      <section className="card">
        <h2>Queue ({agent.queueLength})</h2>
        {agent.queue.length === 0 ? (
          <p className="muted">Nothing waiting. New queued instructions show up here.</p>
        ) : (
          <>
            <ul className="queue-list">
              {agent.queue.map((item) => (
                <li key={item.id}>
                  <div className="queue-list__meta">
                    <span
                      className={`chip ${item.mode === "interrupt" ? "chip--interrupt" : "chip--queue"}`}
                    >
                      {item.mode}
                    </span>
                    <span className="muted">{formatClock(item.createdAt)}</span>
                  </div>
                  <p>{item.text}</p>
                </li>
              ))}
            </ul>
            <p className="muted queue-note">
              Queued items can’t be cancelled from here yet — an interrupt
              replaces the current task but the queue still runs.
            </p>
          </>
        )}
      </section>

      <MarkdownWorkspace
        agentId={agent.id}
        agentName={agent.name}
        agentStatus={agent.status}
        cwd={agent.cwd}
        onAuthLost={markSignedOut}
      />

      <section className="card">
        <h2>Model &amp; log detail</h2>
        <div className="quick-models">
          {(resolveAgentProvider(agent) === "codex" ||
          resolveAgentProvider(agent) === "claude"
            ? catalog
            : QUICK_MODELS
          ).map((m) => (
            <button
              key={m.id}
              type="button"
              className={
                "btn btn--ghost" + (agent.model === m.id ? " btn--selected" : "")
              }
              disabled={settingsBusy}
              onClick={() => void onModel(m.id)}
            >
              {m.short}
            </button>
          ))}
        </div>
        <label className="field">
          <span>All models</span>
          <select
            value={agent.model}
            disabled={settingsBusy}
            onChange={(e) => void onModel(e.target.value)}
          >
            {catalog.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
            {catalog.some((m) => m.id === agent.model) ? null : (
              <option value={agent.model}>{displayModel(agent.model)}</option>
            )}
          </select>
        </label>
        <label className="field">
          <span>Log detail</span>
          <div className="segment" role="group" aria-label="Log detail">
            <button
              type="button"
              className={agent.outputMode === "condensed" ? "is-on" : ""}
              onClick={() => void onOutputMode("condensed")}
              disabled={settingsBusy}
            >
              Condensed
            </button>
            <button
              type="button"
              className={agent.outputMode === "full" ? "is-on" : ""}
              onClick={() => void onOutputMode("full")}
              disabled={settingsBusy}
            >
              Full
            </button>
          </div>
        </label>
      </section>

      <details className="card acc">
        <summary className="acc__summary">
          <span>Additional Claude helper</span>
          {agent.claude?.attached ? (
            <span className={`status-pill status-pill--${agent.claude.status}`}>
              {statusLabel(agent.claude.status)}
            </span>
          ) : (
            <span className="muted">off</span>
          )}
        </summary>
        <div className="acc__body">
          <p className="muted">
            Optional helper that runs beside this agent. It starts only when
            you send it a prompt.
          </p>
          {agent.claude?.attached ? (
            <div className="claude__status">
              <span className={`status-pill status-pill--${agent.claude.status}`}>
                {statusLabel(agent.claude.status)}
              </span>
              <p>{agent.claude.headline || "Claude attached"}</p>
            </div>
          ) : (
            <p className="muted">Not running.</p>
          )}
          <MessageBox placeholder="Prompt for Claude Code…" onSend={onClaude} />
          <div className="message-box__actions">
            <button
              type="button"
              className="btn btn--queue"
              disabled={claudeBusy || !agent.claude?.attached}
              onClick={() => void onStopClaude("queue")}
            >
              Stop after task
            </button>
            <button
              type="button"
              className="btn btn--interrupt"
              disabled={claudeBusy || !agent.claude?.attached}
              onClick={() => void onStopClaude("interrupt")}
            >
              Stop now
            </button>
          </div>
        </div>
      </details>

      <details className="card acc">
        <summary className="acc__summary">
          <span>Workspace folder</span>
          <span className="muted acc__hint">{agent.cwd || "default"}</span>
        </summary>
        <div className="acc__body">
          <p className="muted">
            This is the starting folder for the agent. It can also access other folders allowed by the worker account.
          </p>
          <label className="field">
            <span>Folder</span>
            <input
              value={cwdDraft}
              onChange={(e) => setCwdDraft(e.target.value)}
              placeholder="~/code/my-project"
              autoComplete="off"
              disabled={settingsBusy}
            />
          </label>
          <button
            type="button"
            className="btn btn--accent"
            disabled={
              settingsBusy || !cwdDraft.trim() || cwdDraft.trim() === agent.cwd
            }
            onClick={() => void onCwd()}
          >
            Use this folder
          </button>
        </div>
      </details>

      <nav className="agent-switch" aria-label="Agents">
        {mergeAgentLists(agents).map((a) => (
          <Link
            key={a.id}
            className={"chip" + (a.id === agent.id ? " btn--selected" : "")}
            to={`/agents/${encodeURIComponent(a.id)}`}
          >
            {agentWithModel(a.name, a.model)}
          </Link>
        ))}
      </nav>
    </div>
  );
}
